import { getGoogleToken } from '../api/ctrag';
import { IS_STAGING } from './config';

/**
 * Google Picker 配置管理工具
 * 統一管理所有 Google Picker 的配置
 */

// Drive Picker Element 預設設定
export const DRIVE_SCOPE = 'https://www.googleapis.com/auth/drive.file';
export const DEFAULT_VIEW_ID = 'SHARED_DRIVES';
export const DEFAULT_LOCALE = 'zh-TW';

// 共用的 MIME 類型定義
export const MIME_TYPES = {
  // Google Drive 原生類型
  FOLDER: 'application/vnd.google-apps.folder',
  DOCUMENT: 'application/vnd.google-apps.document',
  SPREADSHEET: 'application/vnd.google-apps.spreadsheet',
  PRESENTATION: 'application/vnd.google-apps.presentation',
  
  // 文件類型
  PDF: 'application/pdf',
  WORD: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  WORD_LEGACY: 'application/msword',
  EXCEL: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  EXCEL_LEGACY: 'application/vnd.ms-excel',
  CSV: 'text/csv',
  POWERPOINT: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  
  // 文字和圖片
  TEXT: 'text/plain',
  JPEG: 'image/jpeg',
  PNG: 'image/png',
  
  // 音視頻
  MP4: 'video/mp4',
  MPEG: 'video/mpeg',
  WEBM: 'video/webm',
  MP3: 'audio/mpeg',
  WAV: 'audio/wav',
  M4A: 'audio/x-m4a'
};

// 預定義的 MIME 類型組合
export const MIME_TYPE_GROUPS = {
  // 資料夾選擇
  FOLDERS_ONLY: [MIME_TYPES.FOLDER],
  
  // 文件選擇（用於 Knowledge Base）
  DOCUMENTS: [
    MIME_TYPES.FOLDER,
    MIME_TYPES.DOCUMENT,
    MIME_TYPES.PDF
  ],
  
  // 辦公文件（用於翻譯）
  OFFICE_DOCUMENTS: [
    MIME_TYPES.WORD,
    MIME_TYPES.WORD_LEGACY,
    MIME_TYPES.PDF,
    MIME_TYPES.EXCEL,
    MIME_TYPES.EXCEL_LEGACY,
    MIME_TYPES.CSV,
    MIME_TYPES.TEXT
  ],
  
  // 會議相關文件（用於會議摘要）
  MEETING_DOCUMENTS: [
    MIME_TYPES.POWERPOINT,
    MIME_TYPES.WORD,
    MIME_TYPES.PDF,
    MIME_TYPES.TEXT,
    MIME_TYPES.EXCEL,
    MIME_TYPES.EXCEL_LEGACY,
    MIME_TYPES.CSV
  ],
  
  // 音視頻文件（用於會議摘要）
  MEDIA_FILES: [
    MIME_TYPES.MP4,
    MIME_TYPES.MPEG,
    MIME_TYPES.WEBM,
    MIME_TYPES.MP3,
    MIME_TYPES.WAV,
    MIME_TYPES.M4A
  ],
  
  // 測試預覽文件
  TEST_FILES: [
    MIME_TYPES.PDF,
    MIME_TYPES.WORD,
    MIME_TYPES.WORD_LEGACY,
    MIME_TYPES.TEXT,
    MIME_TYPES.JPEG,
    MIME_TYPES.PNG
  ]
};

/**
 * 確保單例的 Drive Picker Web Component 存在，並套用視圖設定
 * @param {Object} options
 * @param {string} [options.elementId='aidp-drive-picker'] - DOM 元素 id
 * @param {string|string[]} [options.mimeTypes] - 要顯示的 mime types
 * @param {boolean} [options.includeFolders=true]
 * @param {boolean} [options.enableDrives=true]
 * @param {string} [options.viewId=DEFAULT_VIEW_ID]
 * @param {boolean} [options.selectFolderEnabled=false]
 * @param {string} [options.title='Select from Google Drive']
 * @param {string} [options.locale=DEFAULT_LOCALE]
 * @param {string} [options.developerKey=process.env.REACT_APP_GOOGLE_API_KEY]
 * @param {string} [options.appId=process.env.REACT_APP_GOOGLE_APP_ID]
 * @param {string} [options.clientId=process.env.REACT_APP_GOOGLE_CLIENT_ID]
 * @param {string} [options.scope=DRIVE_SCOPE]
 * @returns {Promise<HTMLElement>}
 */
export async function ensureDrivePickerElement(options = {}) {
  const {
    elementId = 'aidp-drive-picker',
    mimeTypes,
    includeFolders = true,
    enableDrives = true,
    viewId = DEFAULT_VIEW_ID,
    selectFolderEnabled = false,
    title = 'Select from Google Drive',
    locale = DEFAULT_LOCALE,
    developerKey = process.env.REACT_APP_GOOGLE_API_KEY,
    appId = process.env.REACT_APP_GOOGLE_APP_ID,
    clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID,
    scope = DRIVE_SCOPE,
  } = options;

  // 動態載入 web component（若已載入則忽略）
  try {
    await import('@googleworkspace/drive-picker-element');
  } catch (_) {}

  let el = document.getElementById(elementId);
  if (!el) {
    el = document.createElement('drive-picker');
    el.id = elementId;
    el.style.display = 'none';
    document.body.appendChild(el);
    const view = document.createElement('drive-picker-docs-view');
    el.appendChild(view);
  }

  // 設定 Drive Picker 屬性
  try {
    el.setAttribute('title', title);
    el.setAttribute('locale', locale);
    if (developerKey) el.setAttribute('developer-key', developerKey);
    if (appId) el.setAttribute('app-id', appId);
    // 必要屬性：client-id 和 scope
    if (clientId) el.setAttribute('client-id', clientId);
    if (scope) el.setAttribute('scope', scope);
    // 確保初始為不顯示
    el.visible = false;
  } catch (_) {}

  // 設定 Docs View 屬性
  const viewEl = el.querySelector('drive-picker-docs-view');
  if (viewEl) {
    if (mimeTypes) {
      const types = Array.isArray(mimeTypes) ? mimeTypes.join(',') : mimeTypes;
      viewEl.setAttribute('mime-types', types);
    }
    if (includeFolders) viewEl.setAttribute('include-folders', 'true');
    if (enableDrives) viewEl.setAttribute('enable-drives', 'true');
    if (viewId) viewEl.setAttribute('view-id', viewId);
    if (selectFolderEnabled) viewEl.setAttribute('select-folder-enabled', 'true');
  }

  return el;
}

/**
 * 使用 Drive Picker Element (v0.6.2+) 以 Promise 方式開啟選取器
 * @param {Object} options
 * @param {string|string[]} [options.mimeTypes]
 * @param {boolean} [options.includeFolders=true]
 * @param {boolean} [options.enableDrives=true]
 * @param {string} [options.viewId=DEFAULT_VIEW_ID]
 * @param {boolean} [options.selectFolderEnabled=false]
 * @param {string} [options.title='Select from Google Drive']
 * @param {string} [options.locale=DEFAULT_LOCALE]
 * @param {string} [options.oauthToken] - 已有的 OAuth token，未提供則自動取得
 * @returns {Promise<{docs: any[]} | null>} - 成功回傳 { docs }，取消回傳 null
 */
export async function openDrivePicker(options = {}) {
  const {
    mimeTypes,
    includeFolders = true,
    enableDrives = true,
    viewId = DEFAULT_VIEW_ID,
    selectFolderEnabled = false,
    title = 'Select from Google Drive',
    locale = DEFAULT_LOCALE,
    oauthToken,
  } = options;

  const el = await ensureDrivePickerElement({
    mimeTypes,
    includeFolders,
    enableDrives,
    viewId,
    selectFolderEnabled,
    title,
    locale,
  });

  // 設定 token（attribute + property），必要以避免時序問題
  let token = oauthToken;
  if (!token) {
    token = await getGoogleToken();
  }
  if (!token) {
    throw new Error('Failed to get Google OAuth token');
  }
  try {
    el.setAttribute('oauth-token', token);
    el.oauthToken = token;
  } catch (_) {}

  // 以 Promise 包裝事件
  return new Promise((resolve, reject) => {
    const handlePicked = (e) => {
      cleanup();
      resolve({ docs: e?.detail?.docs || [] });
    };
    const handleCanceled = () => {
      cleanup();
      resolve(null);
    };
    const handleError = (e) => {
      cleanup();
      reject(e?.detail || e);
    };

    const cleanup = () => {
      el.removeEventListener('picker-picked', handlePicked);
      el.removeEventListener('picker-canceled', handleCanceled);
      el.removeEventListener('picker-error', handleError);
      el.removeEventListener('picker-oauth-error', handleError);
      // 隱藏 picker
      el.visible = false;
    };

    el.addEventListener('picker-picked', handlePicked);
    el.addEventListener('picker-canceled', handleCanceled);
    el.addEventListener('picker-error', handleError);
    el.addEventListener('picker-oauth-error', handleError);

    // 顯示選取器
    requestAnimationFrame(() => {
      el.visible = true;
    });
  });
}

/**
 * 便利方法：選一個資料夾
 * @param {Object} options
 * @param {string} [options.title]
 * @param {string} [options.locale]
 * @returns {Promise<any|null>} 選到的單一 folder (或 null)
 */
export async function pickDriveFolder({ title = 'Select Folder', locale } = {}) {
  const result = await openDrivePicker({
    includeFolders: true,
    enableDrives: true,
    selectFolderEnabled: true,
    viewId: DEFAULT_VIEW_ID,
    title,
    locale,
  });
  return result?.docs?.[0] || null;
}

/**
 * 便利方法：選一個檔案（單選）
 * @param {Object} options
 * @param {string[]|string} options.mimeTypes
 * @param {string} [options.title]
 * @param {string} [options.locale]
 * @returns {Promise<any|null>} 選到的單一 file (或 null)
 */
export async function pickSingleDriveFile({ mimeTypes, title = 'Select File', locale } = {}) {
  const result = await openDrivePicker({
    mimeTypes,
    includeFolders: true,
    enableDrives: true,
    viewId: DEFAULT_VIEW_ID,
    title,
    locale,
  });
  return result?.docs?.[0] || null;
}

/**
 * 便利方法：選多個檔案（多選）
 * @param {Object} options
 * @param {string[]|string} options.mimeTypes
 * @param {string} [options.title]
 * @param {string} [options.locale]
 * @returns {Promise<any[]>} 選到的檔案陣列（可能為空陣列）
 */
export async function pickMultipleDriveFiles({ mimeTypes, title = 'Select Files', locale } = {}) {
  const result = await openDrivePicker({
    mimeTypes,
    includeFolders: true,
    enableDrives: true,
    viewId: DEFAULT_VIEW_ID,
    title,
    locale,
  });
  return result?.docs || [];
}

// Picker 視圖配置
export const VIEW_CONFIGS = {
  // 資料夾選擇視圖
  FOLDER_VIEW: {
    viewId: window.google?.picker?.ViewId?.FOLDERS,
    enableFolderSelection: true,
    mode: window.google?.picker?.DocsViewMode?.LIST,
    mimeTypes: MIME_TYPE_GROUPS.FOLDERS_ONLY,
    enableTeamDrives: !IS_STAGING,
    includeFolders: true
  },
  
  // 文件選擇視圖
  DOCUMENT_VIEW: {
    mode: window.google?.picker?.DocsViewMode?.LIST,
    enableTeamDrives: !IS_STAGING,
    includeFolders: true
  }
};

// Picker Builder 基礎配置
export const BASE_PICKER_CONFIG = {
  developerKey: process.env.REACT_APP_GOOGLE_API_KEY,
  locale: 'zh-TW',
  features: IS_STAGING ? [] : [
    window.google?.picker?.Feature?.SUPPORT_TEAM_DRIVES
  ]
};

/**
 * 創建 Google Picker 實例
 * @param {Object} config - Picker 配置
 * @param {Function} config.callback - 選擇回調函數
 * @param {string} config.title - Picker 標題
 * @param {string} config.viewType - 視圖類型 ('folder' | 'document' | 'custom')
 * @param {Array<string>} config.mimeTypes - 自定義 MIME 類型
 * @param {Object} config.viewConfig - 自定義視圖配置
 * @param {string} config.token - OAuth token (可選，如果不提供會自動獲取)
 * @param {boolean} config.multiSelect - 是否啟用多選 (默認 false)
 * @returns {Promise<Object>} Picker 實例
 */
export async function createGooglePicker(config) {
  const {
    callback,
    title = 'Select from Google Drive',
    viewType = 'document',
    mimeTypes = null,
    viewConfig = {},
    token = null,
    multiSelect = false
  } = config;

  try {
    // 獲取 OAuth token
    const oauthToken = token || await getGoogleToken();
    if (!oauthToken) {
      throw new Error('Failed to get Google OAuth token');
    }

    // 確保 Google Picker API 已加載
    if (!window.google?.picker) {
      throw new Error('Google Picker API not loaded');
    }

    // 創建視圖
    let view;
    
    if (viewType === 'folder') {
      // 資料夾選擇視圖
      view = new window.google.picker.DocsView(window.google.picker.ViewId.FOLDERS)
        .setSelectFolderEnabled(true)
        .setMode(window.google.picker.DocsViewMode.LIST)
        .setMimeTypes(MIME_TYPE_GROUPS.FOLDERS_ONLY.join(','))
        .setIncludeFolders(true);
      
      // Staging 環境特殊處理
      if (IS_STAGING) {
        view.setParent('root');
      } else {
        view.setEnableTeamDrives(true);
      }
    } else if (viewType === 'custom' && viewConfig) {
      // 自定義視圖
      view = new window.google.picker.DocsView();
      
      if (viewConfig.mode) {
        view.setMode(viewConfig.mode);
      }
      if (viewConfig.includeFolders) {
        view.setIncludeFolders(true);
      }
      if (mimeTypes || viewConfig.mimeTypes) {
        const types = mimeTypes || viewConfig.mimeTypes;
        view.setMimeTypes(Array.isArray(types) ? types.join(',') : types);
      }
      
      // Staging 環境特殊處理
      if (IS_STAGING) {
        view.setParent('root');
      } else if (viewConfig.enableTeamDrives) {
        view.setEnableTeamDrives(true);
      }
    } else {
      // 默認文件選擇視圖
      view = new window.google.picker.DocsView()
        .setMode(window.google.picker.DocsViewMode.LIST)
        .setIncludeFolders(true);
      
      if (mimeTypes) {
        view.setMimeTypes(Array.isArray(mimeTypes) ? mimeTypes.join(',') : mimeTypes);
      }
      
      // Staging 環境特殊處理
      if (IS_STAGING) {
        view.setParent('root');
      } else {
        view.setEnableTeamDrives(true);
      }
    }

    // 創建 Picker Builder
    const pickerBuilder = new window.google.picker.PickerBuilder()
      .setOAuthToken(oauthToken)
      .setDeveloperKey(BASE_PICKER_CONFIG.developerKey)
      .setCallback(callback)
      .addView(view)
      .setLocale(BASE_PICKER_CONFIG.locale);

    // 添加功能
    BASE_PICKER_CONFIG.features.forEach(feature => {
      if (feature) {
        pickerBuilder.enableFeature(feature);
      }
    });

    // 如果啟用多選
    if (multiSelect && window.google?.picker?.Feature?.MULTISELECT_ENABLED) {
      pickerBuilder.enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED);
    }

    // 設置標題
    if (title) {
      pickerBuilder.setTitle(title);
    }

    // 構建並返回 Picker
    const picker = pickerBuilder.build();
    return picker;
    
  } catch (error) {
    console.error('Failed to create Google Picker:', error);
    throw error;
  }
}

/**
 * 加載 Google Picker API
 * @returns {Promise<void>}
 */
export function loadGooglePickerAPI() {
  return new Promise((resolve, reject) => {
    // 檢查是否已經加載
    if (window.gapi && window.google?.picker) {
      resolve();
      return;
    }

    // 創建腳本標籤
    const script = document.createElement('script');
    script.src = 'https://apis.google.com/js/api.js';
    script.onload = () => {
      window.gapi.load('picker', {
        callback: () => {
          console.log('Google Picker API loaded');
          resolve();
        },
        onerror: (error) => {
          console.error('Failed to load Google Picker API:', error);
          reject(error);
        }
      });
    };
    script.onerror = reject;
    document.body.appendChild(script);
  });
}

/**
 * 預定義的 Picker 配置
 */
export const PICKER_PRESETS = {
  // 資料夾選擇器（用於 Knowledge Base 和 Document Parser）
  FOLDER_PICKER: {
    viewType: 'folder',
    title: 'Select Folder'
  },
  
  // 文件選擇器（用於 Chat Input）
  DOCUMENT_PICKER: {
    viewType: 'document',
    mimeTypes: MIME_TYPE_GROUPS.DOCUMENTS,
    title: 'Select Files'
  },
  
  // 辦公文件選擇器（用於翻譯）
  OFFICE_PICKER: {
    viewType: 'document',
    mimeTypes: MIME_TYPE_GROUPS.OFFICE_DOCUMENTS,
    title: 'Select Documents'
  },
  
  // 會議文件選擇器
  MEETING_DOCUMENT_PICKER: {
    viewType: 'document',
    mimeTypes: MIME_TYPE_GROUPS.MEETING_DOCUMENTS,
    title: 'Select Meeting Documents'
  },
  
  // 媒體文件選擇器
  MEDIA_PICKER: {
    viewType: 'document',
    mimeTypes: MIME_TYPE_GROUPS.MEDIA_FILES,
    title: 'Select Media Files'
  },
  
  // 測試文件選擇器
  TEST_PICKER: {
    viewType: 'document',
    mimeTypes: MIME_TYPE_GROUPS.TEST_FILES,
    title: 'Select Test Files'
  }
};

/**
 * 處理 Picker 回調的通用函數
 * @param {Object} data - Picker 回調數據
 * @param {Function} onPicked - 選擇成功的處理函數
 * @param {Function} onCancel - 取消選擇的處理函數
 */
export function handlePickerCallback(data, onPicked, onCancel) {
  if (data.action === window.google.picker.Action.PICKED) {
    if (onPicked) {
      onPicked(data.docs);
    }
  } else if (data.action === window.google.picker.Action.CANCEL) {
    if (onCancel) {
      onCancel();
    }
  }
}

/**
 * 檢查文件是否為資料夾
 * @param {Object} file - Google Drive 文件對象
 * @returns {boolean}
 */
export function isFolder(file) {
  return file.mimeType === MIME_TYPES.FOLDER;
}

/**
 * 檢查文件類型是否在允許列表中
 * @param {Object} file - Google Drive 文件對象
 * @param {Array<string>} allowedTypes - 允許的 MIME 類型列表
 * @returns {boolean}
 */
export function isAllowedFileType(file, allowedTypes) {
  return allowedTypes.includes(file.mimeType);
}

const googlePickerConfig = {
  createGooglePicker,
  loadGooglePickerAPI,
  handlePickerCallback,
  isFolder,
  isAllowedFileType,
  MIME_TYPES,
  MIME_TYPE_GROUPS,
  VIEW_CONFIGS,
  BASE_PICKER_CONFIG,
  PICKER_PRESETS,
  // 新增：Drive Picker Element API
  DRIVE_SCOPE,
  DEFAULT_VIEW_ID,
  DEFAULT_LOCALE,
  ensureDrivePickerElement,
  openDrivePicker,
  pickDriveFolder,
  pickSingleDriveFile,
  pickMultipleDriveFiles
};

export default googlePickerConfig;